import Disconnection from "98Resources/Disconnection.svg";
import Expired from "98Resources/Expired.svg";
import Leak from "98Resources/Leak.svg";
import LeakOff from "98Resources/LeakOff.svg";
import NoDevice from "98Resources/NoDevice.svg";
import None from "98Resources/None.svg";
import PreExpired from "98Resources/PreExpired.svg";
import RTC from "98Resources/RTC.svg";
import TestNg from "98Resources/TestNg.svg";
import TestOk from "98Resources/TestOk.svg";

/**
 * データの数値化
 * @param data 実数の数値
 * @param defaultValue エラー時に返す数値
 */
export const ParseDataInt = (data: string, defaultValue: number) => {
  if (!data) {
    return defaultValue;
  }
  const num = parseInt(data?.trim());
  if (isNaN(num)) {
    return defaultValue;
  }
  return num;
};

/**
 * データの数値化
 * @param data 実数の数値
 * @param defaultValue エラー時に返す数値
 */
export const ParseDataFloat = (data: string, defaultValue: number) => {
  if (!data) {
    return defaultValue;
  }
  const num = parseFloat(data?.trim());
  if (isNaN(num)) {
    return defaultValue;
  }
  return num;
};

export type SensorStatusType = {
  priority: number;
  icon: any;
  textBuilding: string;
  textSensor: string;
  textPico3: string;
};
/**
 * ステータス
 */
export const LeakageExpression: { [status: string]: SensorStatusType } = {
  LEAK: {
    priority: 100,
    icon: Leak,
    textBuilding: "漏水検知",
    textSensor: "漏水検知",
    textPico3: "漏水検知",
  },
  LEAKOFF: {
    priority: 50,
    icon: LeakOff,
    textBuilding: "漏水なし",
    textSensor: "漏水なし",
    textPico3: "漏水なし",
  },
  OK: {
    priority: 60,
    icon: TestOk,
    textBuilding: "テストOK",
    textSensor: "テストOK",
    textPico3: "OK",
  },
  NG: {
    priority: 95,
    icon: TestNg,
    textBuilding: "NG",
    textSensor: "断線",
    textPico3: "テストNG",
  },
  DISCONNECTION: {
    priority: 90,
    icon: Disconnection,
    textBuilding: "通信途絶",
    textSensor: "通信途絶",
    textPico3: "通信途絶",
  },
  RTC: {
    priority: 40,
    icon: RTC,
    textBuilding: "定期通信",
    textSensor: "定期通信",
    textPico3: "定期通信",
  },
  NONE: {
    priority: 60,
    icon: None,
    textBuilding: "データ取得失敗",
    textSensor: "データ取得失敗",
    textPico3: "データ取得失敗",
  },
  PRE_EXPIRED: {
    priority: 45,
    icon: PreExpired,
    textBuilding: "使用期限切れ間近",
    textSensor: "使用期限切れ間近",
    textPico3: "使用期限切れ間近",
  },
  EXPIRED: {
    priority: 46,
    icon: Expired,
    textBuilding: "使用期限切れ",
    textSensor: "使用期限切れ",
    textPico3: "使用期限切れ",
  },
  ILLEGAL: {
    priority: 65,
    icon: PreExpired,
    textBuilding: "不正STATUS",
    textSensor: "不正STATUS",
    textPico3: "不正STATUS",
  },
  DUMMY: {
    priority: 0,
    icon: NoDevice,
    textBuilding: "センサを登録してください",
    textSensor: "センサを登録してください",
    textPico3: "センサを登録してください",
  },
};

export const GetDateForDisp = (date: string) => {
  const d = date.trim();
  return d ? `${d.slice(0, 4)}/${d.slice(4, 6)}/${d.slice(6, 8)}` : "";
};

export const GetDateForLogAPI = (
  start: string,
  end: string
): { start: string; end: string } => {
  const s = start.trim();
  const e = end.trim();
  return {
    start: s ? `${s.slice(0, 4)}${s.slice(5, 7)}${s.slice(8, 10)}T000000` : "",
    end: e ? `${e.slice(0, 4)}${e.slice(5, 7)}${e.slice(8, 10)}T235959` : "",
  };
};

/**
 * 機材番号のタイプ
 */
export type TypeKizaiKind = "init" | "error" | "pico3" | "lte" | "subgiga";

/**
 * 入力された機材番号の種別をチェックする
 * @param data 機材番号
 * @returns 種別
 */
export const CheckKizaiID = (data: string): TypeKizaiKind => {
  if (data.length < 16) {
    return "error";
  }
  // 機材番号フォーマット: [仕向けコード2桁]+[デバイス種別2桁]+[機材番号16/12桁]
  if (data.startsWith("00", 2)) {
    // インデックス2から比較する
    return CheckKizaiIDFormat("pico3", data) ? "pico3" : "error";
  }
  if (data.startsWith("01", 2)) {
    return CheckKizaiIDFormat("lte", data) ? "lte" : "error";
  }
  if (data.startsWith("02", 2)) {
    return CheckKizaiIDFormat("subgiga", data) ? "subgiga" : "error";
  }
  return "error";
};

export const CheckKizaiIDFormat = (kind: TypeKizaiKind, data: string) => {
  if (kind === "pico3") {
    return data.match(/^[a-zA-Z0-9]{16}$/) !== null;
  }
  if (kind === "lte") {
    return data.match(/^[a-zA-Z0-9]{22}$/) !== null;
  }
  if (kind === "subgiga") {
    return data.match(/^[a-zA-Z0-9]{20}$/) !== null;
  }
  return false;
};

export const FormatDate = (dt: string) => {
  if (!dt || dt.length !== 14) {
    return "-";
  }
  return `${dt.slice(0, 4)}/${dt.slice(4, 6)}/${dt.slice(6, 8)} ${dt.slice(8, 10)}:${dt.slice(10, 12)}:${dt.slice(12, 14)}`;
};

export const FormatDateISO = (dt: string) => {
  if (!dt || dt.length !== 14) {
    return "";
  }
  return `${dt.slice(0, 4)}-${dt.slice(4, 6)}-${dt.slice(6, 8)}T${dt.slice(8, 10)}:${dt.slice(10, 12)}:${dt.slice(12, 14)}`;
};

export const ComStatusExpression = (status: string) => {
  if (!status) {
    return "×";
  }
  switch (status.toLowerCase()) {
    case "normal":
      return "○";
    case "disconnection":
      return "×";
    default:
      return "×";
  }
};

/**
 * 機材ID取得(カメラ用)
 * @param url QRコードから取得したURL
 */
export const GetIDFromURL = (url: string) => {
  const posQ = url.indexOf("?");
  if (posQ < 0) {
    return "";
  }
  const id = url.substring(posQ).match(/id=(.*?)(&|$)/);
  if (!id || id.length < 2) {
    return "";
  }
  return id[1].trim();
};

/**
 * 詳細一覧用
 */
export type TypeDetails = { title: string; value: string };
export type TypeDetailsList = TypeDetails[];

/**
 * ログテーブル用日時フォーマット
 */
const logFormat = new Intl.DateTimeFormat("ja-JP", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

/**
 * ログテーブル表示データをフォーマット
 * @param col 列名
 * @param value 素の値
 * @returns
 */
export const formatCellValue = (col: string, value: any) => {
  if (value === undefined) {
    return "-";
  } else if (Array.isArray(value)) {
    return value.join("\n");
  } else {
    switch (col) {
      case "ReceiveTime":
        return FormatDate(value);
      case "DeadLine":
        return logFormat.format(new Date(value * 1000));

      default:
        return value;
    }
  }
};

/**
 * Checksum 検証
 * @param id 機材ID（最後3桁がchecksum）
 * @returns true: success, false: fail
 */
export const isValidChecksum = (id: string): boolean => {
  const data = id.slice(0, -3);
  const cs = parseInt(id.slice(-3), 16);
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += parseInt(data[i], 16);
  }
  return sum === cs;
};

if (import.meta.vitest) {
  const { test, expect } = import.meta.vitest;
  test("checksum verify - success", () => {
    expect(isValidChecksum("AD010123456789ABCDE081")).toBeTruthy();
  });
  test("checksum verify - fail, in not match", () => {
    expect(isValidChecksum("AD010123456789ABCDE082")).toBeFalsy();
  });
  test("checksum verify - fail, includes NaN", () => {
    expect(isValidChecksum("XX010123456789ABCDEXYZ")).toBeFalsy();
  });
}
